import { memo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import LeadStatusControls from './LeadStatusControls';
import ReportActions from './ReportActions';
import { STABILITY_LEVELS, NEW_LEAD_STATUS } from '@/lib/constants';
import { formatMytShort } from '@/lib/datetime';

// Module-level stability → tailwind class map. Stable reference, no recompute.
const STABILITY_COLOR_MAP = {
  [STABILITY_LEVELS.HIGH]: 'text-green-700 bg-green-50',
  [STABILITY_LEVELS.MODERATE]: 'text-amber-700 bg-amber-50',
  [STABILITY_LEVELS.LOW]: 'text-red-700 bg-red-50',
};
const DEFAULT_STABILITY_COLOR = 'text-red-700 bg-red-50';

const COLUMNS = ['Organization', 'Contact', 'Email', 'Completed (MYT)', 'Score', 'Stability', 'Payment', 'Lead Status', 'Actions'];

const StabilityBadge = ({ level }) => (
  <span className={`px-2 py-1 rounded text-xs font-semibold ${STABILITY_COLOR_MAP[level] || DEFAULT_STABILITY_COLOR}`}>
    {level}
  </span>
);

const NewBadge = () => (
  <span
    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-teal-600 text-white animate-pulse"
    data-testid="new-lead-badge"
  >
    <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
    New
  </span>
);

const PaymentBadge = ({ status, downloaded, previewUnlock }) => {
  if (previewUnlock) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-500 text-white"
        data-testid="payment-preview-badge"
        title="Admin preview unlock — not a real payment"
      >
        Preview
      </span>
    );
  }
  if (status === 'paid') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-emerald-600 text-white" data-testid="payment-paid-badge">
        Paid {downloaded ? '· DL' : ''}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-slate-200 text-slate-600">
      Free
    </span>
  );
};

const AssessmentRow = memo(({ row, idx, onStatusChange, onChanged }) => {
  const isNew = row.lead_status === NEW_LEAD_STATUS;
  return (
    <TableRow
      data-testid={`assessment-row-${idx}`}
      data-is-new={isNew ? 'true' : 'false'}
      className={isNew ? 'bg-teal-50/40' : ''}
    >
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          <span>{row.organization}</span>
          {isNew && <NewBadge />}
        </div>
      </TableCell>
      <TableCell>
        <div>{row.contact_name}</div>
        <div className="text-xs text-slate-500">{row.phone}</div>
      </TableCell>
      <TableCell className="text-sm">{row.email}</TableCell>
      <TableCell className="text-sm" data-testid={`assessment-completed-${idx}`}>
        {(() => {
          const { date, time } = formatMytShort(row.created_at);
          return (
            <div>
              <div className="font-medium text-slate-800">{date}</div>
              <div className="text-xs text-slate-500">{time}</div>
            </div>
          );
        })()}
      </TableCell>
      <TableCell><span className="font-bold text-lg">{row.overall_score}</span></TableCell>
      <TableCell><StabilityBadge level={row.stability_level} /></TableCell>
      <TableCell>
        <PaymentBadge
          status={row.payment_status}
          downloaded={row.pdf_downloaded}
          previewUnlock={row.preview_unlock}
        />
      </TableCell>
      <TableCell>
        <LeadStatusControls
          assessmentId={row.id}
          value={row.lead_status}
          onChange={onStatusChange}
        />
      </TableCell>
      <TableCell>
        <ReportActions
          assessmentId={row.id}
          rowIndex={idx}
          paymentStatus={row.payment_status}
          previewUnlock={row.preview_unlock}
          onChanged={onChanged}
        />
      </TableCell>
    </TableRow>
  );
});

AssessmentRow.displayName = 'AssessmentRow';

const EmptyState = () => (
  <TableRow>
    <TableCell colSpan={COLUMNS.length} className="text-center py-12 text-slate-500">
      No assessments found
    </TableCell>
  </TableRow>
);

const AssessmentTable = ({ assessments, onStatusChange, onChanged }) => (
  <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
    <Table>
      <TableHeader>
        <TableRow className="bg-slate-50">
          {COLUMNS.map(col => (
            <TableHead key={col} className="font-bold">{col}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {assessments.length === 0
          ? <EmptyState />
          : assessments.map((row, idx) => (
              <AssessmentRow
                key={row.id}
                row={row}
                idx={idx}
                onStatusChange={onStatusChange}
                onChanged={onChanged}
              />
            ))}
      </TableBody>
    </Table>
  </div>
);

export default AssessmentTable;
